import { Directive, Input, ElementRef, HostListener, ComponentRef, OnDestroy, ApplicationRef, Injector, EmbeddedViewRef, TemplateRef, ViewContainerRef } from '@angular/core';
import { NiTooltipComponent } from './ni-tooltip.component';

@Directive({
    selector: '[tooltip]',
    standalone: true
})
export class TooltipDirective implements OnDestroy {
    @Input() tooltip: string | TemplateRef<any> = '';
    @Input() position: 'above' | 'below' | 'left' | 'right' | 'dynamic' = 'dynamic';
    @Input() theme: 'light' | 'dark' = 'dark';
    @Input() showDelay = 0;
    @Input() hideDelay = 0;
    @Input() alwaysVisible = false;

    private componentRef: ComponentRef<NiTooltipComponent> | null = null;
    private showTimeout?: number;
    private hideTimeout?: number;
    private touchTimeout?: number;

    constructor(
        private readonly elementRef: ElementRef, 
        private readonly appRef: ApplicationRef,
        private readonly injector: Injector,
        private readonly viewContainerRef: ViewContainerRef
    ) {}

    @HostListener('mouseenter')
    onMouseEnter(): void {
        if (this.shouldShowTooltip()) {
            this.initializeTooltip();
        }
    }

    @HostListener('mouseleave')
    onMouseLeave(): void {
        this.setHideTooltipTimeout();
    }

    @HostListener('mousemove', ['$event'])
    onMouseMove($event: MouseEvent): void {
        if (this.componentRef !== null && this.position === 'dynamic') {
            this.componentRef.instance.left = $event.clientX;
            this.componentRef.instance.top = $event.clientY;
            
            if (!(this.tooltip instanceof TemplateRef)) {
                this.componentRef.instance.tooltip = this.tooltip;
            }
        }
    }

    @HostListener('touchstart', ['$event'])
    onTouchStart($event: TouchEvent): void {
        $event.preventDefault();
        window.clearTimeout(this.touchTimeout);
        this.touchTimeout = window.setTimeout(() => {
            if (this.shouldShowTooltip()) {
                this.initializeTooltip();
                
                // For dynamic position, update coordinates based on first touch point
                if (this.position === 'dynamic' && this.componentRef !== null && $event.touches.length > 0) {
                    const touch = $event.touches[0];
                    this.componentRef.instance.left = touch.clientX;
                    this.componentRef.instance.top = touch.clientY;
                }
            }
        }, 500); // 500ms long-press to show tooltip
    }

    @HostListener('touchend')
    onTouchEnd(): void {
        window.clearTimeout(this.touchTimeout);
        this.setHideTooltipTimeout();
    }

    @HostListener('touchcancel')
    onTouchCancel(): void {
        window.clearTimeout(this.touchTimeout);
        this.setHideTooltipTimeout();
    }

    private initializeTooltip() {
        if (this.componentRef === null) {
            window.clearTimeout(this.hideTimeout);
            
            this.componentRef = this.viewContainerRef.createComponent(NiTooltipComponent);

            const [tooltipDOMElement] = (this.componentRef.hostView as EmbeddedViewRef<any>).rootNodes;
            document.body.appendChild(tooltipDOMElement);
            
            this.setTooltipComponentProperties();
            
            this.showTimeout = window.setTimeout(() => {
                if (this.componentRef) {
                    this.componentRef.instance.visible = true;
                }
            }, this.showDelay);
        }
    }

    private setTooltipComponentProperties() {
        if (this.componentRef !== null) {
            if (this.tooltip instanceof TemplateRef) {
                this.componentRef.instance.tooltipTemplate = this.tooltip;
                this.componentRef.instance.tooltip = '';
            } else {
                this.componentRef.instance.tooltip = this.tooltip;
                this.componentRef.instance.tooltipTemplate = null;
            }
            
            this.componentRef.instance.position = this.position;
            this.componentRef.instance.theme = this.theme;

            const { left, right, top, bottom } = this.elementRef.nativeElement.getBoundingClientRect();

            switch (this.position) {
                case 'below': {
                    this.componentRef.instance.left = Math.round((right - left) / 2 + left);
                    this.componentRef.instance.top = Math.round(bottom);
                    break;
                }
                case 'above': {
                    this.componentRef.instance.left = Math.round((right - left) / 2 + left);
                    this.componentRef.instance.top = Math.round(top);
                    break;
                }
                case 'right': {
                    this.componentRef.instance.left = Math.round(right);
                    this.componentRef.instance.top = Math.round(top + (bottom - top) / 2);
                    break;
                }
                case 'left': {
                    this.componentRef.instance.left = Math.round(left);
                    this.componentRef.instance.top = Math.round(top + (bottom - top) / 2);
                    break;
                }
                default: {
                    break;
                }
            }
        }
    }

    private setHideTooltipTimeout() {
        window.clearTimeout(this.hideTimeout);
        this.hideTimeout = window.setTimeout(() => this.destroy(), this.hideDelay);
    }

    ngOnDestroy(): void {
        this.destroy();
        window.clearTimeout(this.touchTimeout);
    }

    destroy(): void {
        if (this.componentRef !== null) {
            window.clearTimeout(this.showTimeout);
            window.clearTimeout(this.hideTimeout);
            this.componentRef.destroy();
            this.componentRef = null;
        }
    }

    private shouldShowTooltip(): boolean {
        // Don't show tooltip if it's an empty string
        if (typeof this.tooltip === 'string' && this.tooltip.trim() === '') {
            return false;
        }
    
        // Always show tooltip for template references
        if (this.tooltip instanceof TemplateRef) {
            return true;
        }
    
        // Check truncation only if not "alwaysVisible"
        if (!this.alwaysVisible) {
            const element = this.elementRef.nativeElement;
    
            if (element.scrollWidth > element.clientWidth) {
                return true;
            }
    
            if (element.scrollHeight > element.clientHeight) {
                return true;
            }
    
            return false;
        }
    
        return true;
    }
    
}